import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Save, RotateCcw, Loader2, MessageSquareText, Info } from "lucide-react";
import { toast } from "sonner";

export default function LiveSupportChatTemplates() {
  const [items, setItems] = useState([]);
  const [edited, setEdited] = useState({}); // {key: value}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/live-support/chat-templates");
      setItems(data?.items || []);
      // Reset local edits to the server values
      const init = {};
      (data?.items || []).forEach((it) => { init[it.key] = it.value || ""; });
      setEdited(init);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load templates");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setVal = (key, val) => setEdited((p) => ({ ...p, [key]: val }));

  const dirtyKeys = items.filter((it) => (edited[it.key] || "") !== (it.value || "")).map((it) => it.key);

  const saveAll = async () => {
    if (dirtyKeys.length === 0) { toast.info("No changes"); return; }
    setSaving(true);
    try {
      const payload = {};
      dirtyKeys.forEach((k) => { payload[k] = edited[k] || ""; });
      const { data } = await api.put("/admin/live-support/chat-templates", { templates: payload });
      setItems(data?.items || []);
      const init = {};
      (data?.items || []).forEach((it) => { init[it.key] = it.value || ""; });
      setEdited(init);
      toast.success(`Saved ${dirtyKeys.length} template${dirtyKeys.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const resetOne = async (key) => {
    if (!window.confirm("Reset this template back to the default text? This will overwrite your current edit.")) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/live-support/chat-templates", { templates: { [key]: "" } });
      setItems(data?.items || []);
      const init = {};
      (data?.items || []).forEach((it) => { init[it.key] = it.value || ""; });
      setEdited(init);
      toast.success("Reset to default");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setSaving(false); }
  };

  const revertLocal = (key) => {
    const it = items.find((x) => x.key === key);
    if (!it) return;
    setVal(key, it.value || "");
  };

  return (
    <div data-testid="ls-chat-templates-page">
      <PageHeader
        title="Live Chat — System Messages"
        subtitle="Edit the auto-text messages the chat widget sends (welcome greeting, agent-join message, queue-expiry message). Changes apply immediately to all new chats."
      />

      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600 inline-flex items-center gap-2">
          <MessageSquareText size={15} className="text-emerald-600" />
          <span>{items.length} editable template{items.length === 1 ? "" : "s"} · <b>{dirtyKeys.length}</b> unsaved</span>
        </div>
        <button
          onClick={saveAll}
          disabled={saving || dirtyKeys.length === 0}
          data-testid="ls-templates-save-all"
          className="h-10 px-4 rounded-sm bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm inline-flex items-center gap-2"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Saving..." : `Save ${dirtyKeys.length || ""} change${dirtyKeys.length === 1 ? "" : "s"}`}
        </button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-gray-500 bg-white border border-gray-200 rounded-sm">
          <Loader2 size={16} className="animate-spin inline mr-2" />Loading templates...
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((it) => {
            const cur = edited[it.key] ?? "";
            const dirty = (cur || "") !== (it.value || "");
            const sameAsDefault = (cur || "").trim() === (it.default || "").trim();
            return (
              <div key={it.key} className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid={`ls-template-card-${it.key}`}>
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-bold text-gray-900 text-[14px] flex items-center gap-2">
                      {it.label}
                      {it.is_custom && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 text-[10px] font-bold uppercase tracking-wider">Custom</span>
                      )}
                      {dirty && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold uppercase tracking-wider" data-testid={`ls-template-dirty-${it.key}`}>Unsaved</span>
                      )}
                    </div>
                    <div className="text-[12px] text-gray-600 mt-0.5">{it.hint}</div>
                    {it.placeholders && it.placeholders.length > 0 && (
                      <div className="text-[11px] text-gray-500 mt-1 inline-flex items-center gap-1 flex-wrap">
                        <Info size={11} /> Placeholders:
                        {it.placeholders.map((p) => (
                          <code key={p} className="bg-gray-100 px-1 rounded text-emerald-700 font-mono">{p}</code>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 inline-flex items-center gap-1">
                    {dirty && (
                      <button onClick={() => revertLocal(it.key)} className="h-8 px-2 rounded-sm border border-gray-200 hover:bg-gray-50 text-xs font-semibold text-gray-700 inline-flex items-center gap-1" data-testid={`ls-template-revert-${it.key}`} title="Discard local edits">
                        Revert
                      </button>
                    )}
                    <button
                      onClick={() => resetOne(it.key)}
                      disabled={saving || (!it.is_custom && sameAsDefault)}
                      className="h-8 px-2 rounded-sm border border-amber-200 text-amber-700 hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-semibold inline-flex items-center gap-1"
                      data-testid={`ls-template-reset-${it.key}`}
                      title="Reset to default text"
                    >
                      <RotateCcw size={11} /> Reset
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <textarea
                    value={cur}
                    onChange={(e) => setVal(it.key, e.target.value)}
                    rows={Math.min(14, Math.max(5, (cur.match(/\n/g) || []).length + 3))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-sm text-[13.5px] leading-relaxed font-mono whitespace-pre-wrap bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:border-emerald-500"
                    data-testid={`ls-template-text-${it.key}`}
                  />
                  {!sameAsDefault && (
                    <details className="mt-2">
                      <summary className="text-[11px] text-gray-500 cursor-pointer hover:text-gray-700">View default text</summary>
                      <pre className="mt-1.5 p-2 bg-gray-50 border border-gray-200 rounded text-[11.5px] text-gray-600 whitespace-pre-wrap font-mono">{it.default}</pre>
                    </details>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-sm p-4 text-sm text-emerald-900 max-w-4xl">
        <div className="font-bold mb-1">Tips</div>
        <ul className="list-disc list-inside space-y-1 text-emerald-800 text-[13px]">
          <li>Changes take effect immediately on the very next chat — no restart needed.</li>
          <li>Use <code className="bg-white px-1 rounded">{`{agent_name}`}</code> / <code className="bg-white px-1 rounded">{`{user_name}`}</code> only inside templates that list them as placeholders — other curly-brace text is left untouched.</li>
          <li>Click <b>Reset</b> on any card to restore that single template back to the factory default.</li>
          <li>Use line breaks freely — they&apos;re preserved in the chat widget.</li>
        </ul>
      </div>
    </div>
  );
}
